import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Build as BuildIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useEmpresa } from '../context/EmpresaContext';
import config from '../config';

const AdminTools = () => {
  const [stats, setStats] = useState(null);
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [fixDialog, setFixDialog] = useState(false);
  const { user } = useAuth();
  const { simboloMoneda } = useEmpresa();

  useEffect(() => {
    loadStats();
    validateResponsables();
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/api/asignacion-trabajadores/stats/${user.id_empresa}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setStats(response.data);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  const validateResponsables = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/api/asignacion-trabajadores/validate-responsables/${user.id_empresa}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setValidation(response.data);
    } catch (error) {
      console.error('Error al validar responsables:', error);
    } finally {
      setLoading(false);
    }
  };

  const fixResponsables = async () => {
    setFixing(true);
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/api/asignacion-trabajadores/fix-responsables/${user.id_empresa}`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      
      alert(`Reparación completada: ${response.data.ordenes_reparadas} órdenes reparadas`);
      
      // Recargar datos
      await loadStats();
      await validateResponsables();
      setFixDialog(false);
    } catch (error) {
      console.error('Error al reparar responsables:', error);
      alert('Error al realizar la reparación');
    } finally {
      setFixing(false);
    }
  };

  const getEstadoColor = (estado) => {
    switch(estado) {
      case 'CORRECTO': return 'success';
      case 'SIN_RESPONSABLE': return 'error';
      case 'MULTIPLE_RESPONSABLES': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        <BuildIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
        Herramientas de Administración
      </Typography>

      {/* Estadísticas Generales */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                📊 Estadísticas Generales
              </Typography>
              {stats ? (
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="Órdenes con Trabajadores"
                      secondary={stats.ordenes_con_trabajadores}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Total de Asignaciones"
                      secondary={stats.total_asignaciones}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Responsables Activos"
                      secondary={stats.total_responsables}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Trabajadores Activos"
                      secondary={stats.trabajadores_activos}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Precio Promedio"
                      secondary={`${simboloMoneda} ${parseFloat(stats.precio_promedio || 0).toFixed(2)}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Comisión Promedio"
                      secondary={`${parseFloat(stats.comision_promedio || 0).toFixed(1)}%`}
                    />
                  </ListItem>
                </List>
              ) : (
                <CircularProgress />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" color="primary">
                  🔍 Validación de Responsables
                </Typography>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={validateResponsables}
                  disabled={loading}
                  size="small"
                >
                  Actualizar
                </Button>
              </Box>

              {loading ? (
                <CircularProgress />
              ) : validation ? (
                <>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Total de Órdenes
                      </Typography>
                      <Typography variant="h6">
                        {validation.total_ordenes}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Órdenes Correctas
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {validation.ordenes_correctas}
                      </Typography>
                    </Grid>
                  </Grid>

                  {validation.hay_problemas ? (
                    <Alert 
                      severity="warning" 
                      sx={{ mb: 2 }}
                      action={
                        <Button
                          size="small"
                          onClick={() => setFixDialog(true)}
                          startIcon={<BuildIcon />}
                        >
                          Reparar
                        </Button>
                      }
                    >
                      Se encontraron {validation.problemas.length} problemas
                    </Alert>
                  ) : (
                    <Alert severity="success" icon={<CheckCircleIcon />}>
                      ✅ Todas las órdenes tienen responsables correctamente asignados
                    </Alert>
                  )}

                  {validation.problemas.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Problemas encontrados:
                      </Typography>
                      <List dense>
                        {validation.problemas.map((problema) => (
                          <ListItem key={problema.id_orden_servicio}>
                            <ListItemText
                              primary={`Orden #${problema.id_orden_servicio}`}
                              secondary={`${problema.total_trabajadores} trabajadores, ${problema.responsables_count} responsables`}
                            />
                            <Chip 
                              label={problema.estado_responsable.replace('_', ' ')}
                              color={getEstadoColor(problema.estado_responsable)}
                              size="small"
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </>
              ) : null}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Acciones Rápidas */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          ⚡ Acciones Rápidas
        </Typography>
        <Grid container spacing={2}>
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                loadStats();
                validateResponsables();
              }}
              disabled={loading}
            >
              Actualizar Todo
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<AssignmentIcon />}
              onClick={() => window.location.href = '/home/ordenes'}
            >
              Ver Órdenes
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Dialog de Confirmación para Reparar */}
      <Dialog open={fixDialog} onClose={() => setFixDialog(false)}>
        <DialogTitle>
          <WarningIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'warning.main' }} />
          Confirmar Reparación
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Esta acción reparará automáticamente las órdenes que no tienen responsable asignado.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Se asignará como responsable al primer trabajador de cada orden afectada.
          </Typography>
          {validation && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Se repararán {validation.problemas.length} órdenes con problemas.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFixDialog(false)}>Cancelar</Button>
          <Button 
            onClick={fixResponsables}
            variant="contained"
            disabled={fixing}
            startIcon={fixing ? <CircularProgress size={20} /> : <BuildIcon />}
          >
            {fixing ? 'Reparando...' : 'Confirmar Reparación'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminTools;